import { css } from 'aphrodite';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { mainChartColor } from '../../../helpers/chartColors';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { formatDealSizeAxisTick } from '../../helpers/formatDealSizeAxisTick';
import type { DealSizeByStageRow } from '../../helpers/opportunityReportingChartTypes';
import { truncateStageName } from '../../helpers/truncateStageName';
import { styleSheet } from './styles';

const CHART_AXIS_TICK = { fontSize: THEME.typography.fontSizes.xxs, fill: THEME.colors.text.main } as const;

function DealSizeByStageTooltip({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: DealSizeByStageRow & { fill?: string } }>;
}) {
	if (!active || !payload?.length) {
		return null;
	}
	const row = payload[0]?.payload;
	if (!row) {
		return null;
	}
	return (
		<div className={css(styleSheet.tooltip)}>
			<div className={css(styleSheet.tooltipTitle)}>{row.stageName}</div>
			<p className={css(styleSheet.tooltipLine)}>{formatCurrency(row.dealSize)}</p>
			<p className={css(styleSheet.tooltipLine)}>{row.count} opportunities</p>
		</div>
	);
}

interface DealSizeByStageChartProps {
	data: DealSizeByStageRow[];
	isLoading?: boolean;
	className?: string;
}

export const DealSizeByStageChart: React.FC<DealSizeByStageChartProps> = ({ data, isLoading, className }) => {
	const chartData = React.useMemo(
		() =>
			data.map(row => ({
				...row,
				fill: mainChartColor(),
			})),
		[data]
	);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No opportunity data for this board</div>
			</div>
		);
	}

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h2 className={css(styleSheet.title)}>Deal size by stage</h2>
			<ResponsiveContainer width='100%' height={320}>
				<BarChart data={chartData} margin={{ top: 12, right: 16, left: 20, bottom: 92 }}>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='stageName'
						stroke={THEME.colors.text.main}
						tick={CHART_AXIS_TICK}
						interval={0}
						angle={-35}
						textAnchor='end'
						height={88}
						tickMargin={10}
						tickFormatter={(value: string) => truncateStageName(value)}
					/>
					<YAxis stroke={THEME.colors.text.main} tick={CHART_AXIS_TICK} tickFormatter={formatDealSizeAxisTick} />
					<Tooltip content={<DealSizeByStageTooltip />} />
					<Bar dataKey='dealSize' radius={[4, 4, 0, 0]}>
						{chartData.map((entry, index) => (
							<Cell key={`${entry.stageName}-${index}`} fill={entry.fill} />
						))}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};
